import { convertChannel, convertMessage } from './converters'
import type {
  ApiChatServerAttachmentSettings,
  ApiChatServerChannel,
  ApiChatServerMessage,
  ApiChatServerMessageSettings,
  ApiChatServerSettings,
  ApiChatServerUnread,
} from './rest.types'
import type { ChatServerMarkAllReadScope } from './RestClient'
import { ChatServerRestClient } from './RestClient'
import type { ChatServerAction } from './state'
import { setArchiveCount, setChannelsStatus, setUnread, setUnreadBulk, upsertChannel, upsertChannels, upsertMessages } from './state'

export type ChatServerApiOptions = {
  /** chat_server REST base url (direct CORS); null while unconfigured. */
  getBaseUrl: () => string | null
  /** Bearer for the chat_server REST call (the session D1 token). */
  getToken: () => Promise<string>
  /** Rotate the token after a 401 so the call replays once; null = genuine auth loss (see `ChatServerRestClientOptions`). */
  refreshToken?: () => Promise<string | null>
  /** Sink for normalized state updates (the host bridges this into its store). */
  dispatch: (action: ChatServerAction) => void
  /** The current user's chat_server_uid — excluded from participant resolution. */
  getSelfUid: () => string | null
  /** Best-effort, host-side batched resolution of participant external_ids to identities. */
  resolveParticipants?: (externalIds: string[]) => void
  /** Current UI locale, forwarded as `Accept-Language` so error copy comes back localized. */
  getLocale?: () => string | null
  log?: (...args: unknown[]) => void
  /** Injectable for tests; defaults to global fetch. */
  fetch?: typeof fetch
}

/**
 * REST facade over chat_server, assembled from host-injected bindings: fetches
 * JSON via `ChatServerRestClient`, converts payloads, and emits normalized state
 * updates (channel/message/unread loads plus batched participant resolution)
 * through `dispatch`. The host constructs it with store-bound functions — see
 * store/actions/contexts/chatServerApi.
 */
export class ChatServerApi {
  private readonly client: ChatServerRestClient
  private readonly dispatch: (action: ChatServerAction) => void
  private readonly getSelfUid: () => string | null
  private readonly resolveParticipants?: (externalIds: string[]) => void
  private readonly log?: (...args: unknown[]) => void
  private settings: Promise<ApiChatServerSettings> | null = null

  constructor(options: ChatServerApiOptions) {
    this.client = new ChatServerRestClient({
      getBaseUrl: options.getBaseUrl,
      getToken: options.getToken,
      refreshToken: options.refreshToken,
      getLocale: options.getLocale,
      fetch: options.fetch,
    })
    this.dispatch = options.dispatch
    this.getSelfUid = options.getSelfUid
    this.resolveParticipants = options.resolveParticipants
    this.log = options.log
  }

  /** Load the sidebar channel list for a user (with unread counts) into the slices; `archived: true` loads the archive, `limit`/`cursor` page it. */
  async loadUserChannels(
    externalId: string,
    opts?: { archived?: boolean; favorite?: boolean; unread?: boolean; limit?: number; cursor?: string }
  ): Promise<{ channels: ApiChatServerChannel[]; nextCursor: string | null }> {
    // Archive, favorites and unread are side listings: they upsert channels but leave the
    // main list's status and the archive summary alone (the server omits `meta.archive` for all three).
    const sideListing = !!(opts?.archived || opts?.favorite || opts?.unread)
    if (!sideListing) this.dispatch(setChannelsStatus('loading'))
    try {
      const { data, meta } = await this.client.getUserChannels(externalId, opts)
      this.dispatch(upsertChannels(data))
      this.seedLastMessages(data)
      const unread: Record<string, number> = {}
      for (const channel of data) if (typeof channel.unread_count === 'number') unread[channel.id] = channel.unread_count
      this.dispatch(setUnreadBulk(unread))
      if (!sideListing) {
        // `meta.archive` (the "Архив" folder summary) comes only on the first
        // non-archived, non-cursor page.
        if (!opts?.cursor) this.dispatch(setArchiveCount(meta?.archive?.count ?? 0))
        this.dispatch(setChannelsStatus('loaded'))
      }
      // Resolve names/avatars for all participants of all dialogs in one batched
      // call, keyed by external_id (= each user's chat_server_uid).
      this.resolveParticipants?.(this.collectParticipantIds(data))
      this.log?.('loadUserChannels', externalId, opts ?? {}, data.length)
      return { channels: data, nextCursor: meta?.next_cursor ?? null }
    } catch (e) {
      if (!sideListing) this.dispatch(setChannelsStatus('error'))
      this.log?.('loadUserChannels failed', externalId, e)
      throw e
    }
  }

  async loadUserUnread(externalId: string): Promise<ApiChatServerUnread[]> {
    const data = await this.client.getUserUnread(externalId)
    const counts: Record<string, number> = {}
    for (const entry of data) counts[entry.channel_id] = entry.unread_count
    this.dispatch(setUnreadBulk(counts))
    this.log?.('loadUserUnread', externalId, data.length)
    return data
  }

  async loadChannel(id: string): Promise<ApiChatServerChannel> {
    const data = await this.client.getChannel(id)
    this.storeChannel(data)
    this.log?.('loadChannel', id)
    return data
  }

  /**
   * The direct channel with `peerExternalId` that the user archived, stored as the archive listing would; null when there
   * is none. The main listing leaves archived channels out, so a chat opened by its peer alone would start an empty dialog.
   */
  async findArchivedDirectChannel(externalId: string, peerExternalId: string): Promise<ApiChatServerChannel | null> {
    let cursor: string | undefined
    do {
      const { data, meta } = await this.client.getUserChannels(externalId, { archived: true, limit: CHANNELS_PAGE_MAX, cursor })
      const found = data.find(channel => channel.type === 'direct' && channel.participants.some(p => p.user?.external_id === peerExternalId))
      if (found) {
        this.storeChannel(found)
        this.log?.('findArchivedDirectChannel', peerExternalId, found.id)
        return found
      }
      cursor = meta?.next_cursor ?? undefined
    } while (cursor)
    this.log?.('findArchivedDirectChannel', peerExternalId, null)
    return null
  }

  /** Page messages of a channel (`before`/`after` cursors are message ids). */
  async loadMessages(args: { channelId: string; before?: string; after?: string; limit?: number }): Promise<ApiChatServerMessage[]> {
    const data = await this.client.getMessages(args)
    this.dispatch(upsertMessages(args.channelId, data.map(convertMessage)))
    this.log?.('loadMessages', args.channelId, data.length)
    return data
  }

  async sendMessage(args: { channelId: string; body: string; attributes?: Record<string, unknown> }): Promise<ApiChatServerMessage> {
    const data = await this.client.postMessage(args)
    this.dispatch(upsertMessages(args.channelId, [convertMessage(data)]))
    this.log?.('sendMessage', args.channelId, data.id)
    return data
  }

  /** Lazy-create a direct channel on first message (D2). */
  async createChannel(participants: { external_id: string; role?: string }[], type: 'direct' | 'group' | 'support' = 'direct'): Promise<ApiChatServerChannel> {
    const data = await this.client.postChannel({ type, participants })
    const { channel, participants: ps } = convertChannel(data)
    this.dispatch(upsertChannel(channel, ps))
    // A channel born here is the only one nothing else resolves: chat_server
    // never sends name/avatar, so a dialog row has no fallback and renders
    // empty until the next loadUserChannels.
    this.resolveParticipants?.(this.collectParticipantIds([data]))
    this.log?.('createChannel', type, data.id)
    return data
  }

  async editMessage(args: { id: string; channelId: string; body?: string; attributes?: Record<string, unknown> }): Promise<ApiChatServerMessage> {
    const data = await this.client.patchMessage(args)
    this.dispatch(upsertMessages(args.channelId, [convertMessage(data)]))
    this.log?.('editMessage', args.channelId, args.id)
    return data
  }

  /** Soft-delete (cancel) within the server's 30s window; reflects the returned `cancelled_at` into state so the tombstone shows without waiting for the WS `message_cancelled` echo. */
  async deleteMessage(id: string): Promise<ApiChatServerMessage> {
    const data = await this.client.deleteMessage(id)
    this.dispatch(upsertMessages(data.channel_id, [convertMessage(data)]))
    this.log?.('deleteMessage', data.channel_id, id)
    return data
  }

  /** REST fallback for a button press when the channel's socket is down; nothing lands in state. */
  async clickMessageButton(messageId: string, callbackData: string): Promise<void> {
    await this.client.postMessageCallback(messageId, callbackData)
    this.log?.('clickMessageButton', messageId, callbackData)
  }

  /** Authenticated fetch of a `/media/:id?size=` attachment path. */
  async getMediaBlob(path: string, signal?: AbortSignal): Promise<Blob> {
    return await this.client.getMedia(path, signal)
  }

  /** Whole chat settings from the shared fetch; a failure (or fields the server omits) answers config defaults, so callers always get a complete object. */
  getSettings(): Promise<ApiChatServerSettings> {
    return this.fetchSettings().then(normalizeSettings, () => FALLBACK_SETTINGS)
  }

  /** Upload limits from the shared settings fetch; a failure answers the server's own defaults. */
  getAttachmentSettings(): Promise<ApiChatServerAttachmentSettings> {
    return this.getSettings().then(settings => settings.attachments)
  }

  /** Reaction emoji whitelist from the shared settings fetch; a failure answers the server's config defaults. */
  getReactionEmojis(): Promise<string[]> {
    return this.getSettings().then(settings => settings.reaction_emojis ?? FALLBACK_REACTION_EMOJIS)
  }

  /** One memoized `GET /api/settings`; a failed fetch is not cached, so the next caller retries. */
  private fetchSettings(): Promise<ApiChatServerSettings> {
    this.settings ??= this.client.getSettings().catch((e: unknown) => {
      this.settings = null
      this.log?.('getSettings failed', e)
      throw e
    })
    return this.settings
  }

  /** Presign + direct S3 PUT of one file; the returned token goes into the message's `attachment_tokens`. */
  async uploadAttachment(file: Blob & { name: string }, kind: 'photo' | 'document', signal?: AbortSignal): Promise<string> {
    const presign = await this.client.postAttachmentsPresign({
      file_name: file.name,
      content_type: file.type || 'application/octet-stream',
      byte_size: file.size,
      kind,
    })
    await this.client.putPresignedUpload(presign.upload_url, presign.headers, file, signal)
    this.log?.('uploadAttachment', kind, file.name)
    return presign.upload_token
  }

  /** Archive for the current user; upserts the returned channel so the self-participant's `archived_at` flips in the store. */
  /**
   * Mark every channel in `scope` read in one request, instead of a `markRead` push per
   * channel (each of which would join a phoenix topic and never leave it).
   *
   * The server broadcasts `unread_count` per affected channel, but the returned ids are
   * zeroed here too: that is instant, and it keeps the counters honest if the socket is down.
   */
  async markAllRead(externalId: string, scope: ChatServerMarkAllReadScope = 'non_archived'): Promise<string[]> {
    const { channelIds } = await this.client.markAllRead(externalId, scope)
    if (channelIds.length) this.dispatch(setUnreadBulk(Object.fromEntries(channelIds.map(id => [id, 0]))))
    this.log?.('markAllRead', scope, channelIds.length)
    return channelIds
  }

  async archiveChannel(id: string): Promise<void> {
    const data = await this.client.archiveChannel(id)
    const { channel, participants } = convertChannel(data)
    this.dispatch(upsertChannel(channel, participants))
    this.log?.('archiveChannel', id)
  }

  async unarchiveChannel(id: string): Promise<void> {
    const data = await this.client.unarchiveChannel(id)
    const { channel, participants } = convertChannel(data)
    this.dispatch(upsertChannel(channel, participants))
    this.log?.('unarchiveChannel', id)
  }

  setUnread(channelId: string, count: number): void {
    this.dispatch(setUnread(channelId, count))
    this.log?.('setUnread', channelId, count)
  }

  private storeChannel(data: ApiChatServerChannel): void {
    const { channel, participants } = convertChannel(data)
    this.dispatch(upsertChannel(channel, participants))
    if (typeof data.unread_count === 'number') this.dispatch(setUnread(data.id, data.unread_count))
    this.seedLastMessages([data])
    this.resolveParticipants?.(this.collectParticipantIds([data]))
  }

  private seedLastMessages(channels: ApiChatServerChannel[]): void {
    for (const channel of channels) {
      if (channel.last_message) this.dispatch(upsertMessages(channel.id, [convertMessage(channel.last_message)]))
    }
  }

  /** External_ids of all participants across channels, minus the current user. */
  private collectParticipantIds(channels: ApiChatServerChannel[]): string[] {
    const selfUid = this.getSelfUid()
    const ids = new Set<string>()
    for (const channel of channels) {
      for (const participant of channel.participants) {
        const externalId = participant.user?.external_id
        if (externalId === null || externalId === undefined) continue
        if (selfUid !== null && String(externalId) === String(selfUid)) continue
        ids.add(String(externalId))
      }
    }
    return [...ids]
  }
}

/** chat_server's cap on a channel listing page (`@channels_max_limit`). */
const CHANNELS_PAGE_MAX = 100

/** Mirrors chat_server config.exs `:attachments` — used only when `GET /api/settings` fails. */
const FALLBACK_ATTACHMENT_SETTINGS: ApiChatServerAttachmentSettings = {
  photo_mime_types: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
  photo_max_bytes: 10_485_760,
  document_max_bytes: 20_971_520,
  upload_ttl_seconds: 900,
}

/** Mirrors chat_server config.exs `:reaction_emojis` — used only when `GET /api/settings` fails. */
const FALLBACK_REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '🔥', '🎉']

/** Whole-settings default, used when the fetch fails outright. */
const FALLBACK_SETTINGS: ApiChatServerSettings = {
  reaction_emojis: FALLBACK_REACTION_EMOJIS,
  attachments: FALLBACK_ATTACHMENT_SETTINGS,
}

/** Fill config defaults for fields the server omitted (or returned empty) so callers get a complete object. */
const normalizeSettings = (settings: ApiChatServerSettings): ApiChatServerSettings => ({
  reaction_emojis: settings.reaction_emojis?.length ? settings.reaction_emojis : FALLBACK_REACTION_EMOJIS,
  attachments: settings.attachments ?? FALLBACK_ATTACHMENT_SETTINGS,
  // No default to mirror: an unusable window is left absent so callers keep the action
  // available and the server stays the one deciding (see chat_settings.ts).
  messages: normalizeMessageSettings(settings.messages),
})

const normalizeMessageSettings = (messages: ApiChatServerMessageSettings | undefined): ApiChatServerMessageSettings | undefined => {
  const cancel = usableWindow(messages?.cancel_timeout_seconds)
  const edit = usableWindow(messages?.edit_timeout_seconds)
  if (cancel === undefined && edit === undefined) return undefined
  return { ...(cancel === undefined ? {} : { cancel_timeout_seconds: cancel }), ...(edit === undefined ? {} : { edit_timeout_seconds: edit }) }
}

const usableWindow = (seconds: number | undefined): number | undefined =>
  typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0 ? seconds : undefined
